import React, { useState, useEffect } from 'react';
import { Users, Trash2, Edit } from 'lucide-react';
import { getGuests, deleteGuest } from '../api';
import type { Guest } from '../types';

interface GuestListProps {
  onGuestUpdated: () => void;
  onGuestDeleted?: (deletedGuestId: string) => void;
  guests?: Guest[];
  isLoading?: boolean;
  eventId: string;
}

export default function GuestList({ 
  onGuestUpdated, 
  onGuestDeleted, 
  guests: propGuests, 
  isLoading: propIsLoading,
  eventId
}: GuestListProps) {
  const [guests, setGuests] = useState<Guest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const loadGuests = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await getGuests(eventId);
      if (response.success && response.guests) {
        setGuests(response.guests);
      } else {
        setError(response.error || 'Failed to load guests');
      }
    } catch (error) {
      setError('Failed to load guests. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // If guests are provided as props, use them; otherwise load from API
    if (propGuests !== undefined) {
      setGuests(propGuests);
      setIsLoading(false);
    } else {
      loadGuests();
    }
  }, [propGuests]);

  // Use prop loading state if provided, otherwise use local state
  const displayIsLoading = propIsLoading !== undefined ? propIsLoading : isLoading;

  const handleDeleteGuest = async (id: string) => {
    if (!confirm('Are you sure you want to delete this guest?')) {
      return;
    }

    try {
      const response = await deleteGuest(id, eventId);
      if (response.success) {
        // Update local state
        setGuests(guests.filter(guest => guest.id !== id));
        // Call parent callback if provided
        onGuestDeleted?.(id);
        onGuestUpdated();
      } else {
        alert(response.error || 'Failed to delete guest');
      }
    } catch (error) {
      alert('Failed to delete guest. Please try again.');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (displayIsLoading) {
    return (
      <div className="card">
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          Loading guests...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div style={{ 
          color: '#d32f2f', 
          backgroundColor: '#ffebee', 
          padding: '1rem', 
          borderRadius: '8px',
          border: '1px solid #ffcdd2'
        }}>
          {error}
          <button 
            onClick={loadGuests}
            className="btn btn-secondary"
            style={{ marginTop: '1rem' }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginTop: '1rem' }}>
      <h2 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Users size={24} />
        Guest List ({guests.length} guests)
      </h2>

      {guests.length === 0 ? (
        <div className="empty-state">
          <h3>No guests added yet</h3>
          <p>Add your first guest using the form above!</p>
        </div>
      ) : (
        <div className="guest-list">
          {guests
            .sort((a, b) => a.table - b.table || a.name.localeCompare(b.name))
            .map((guest) => (
              <div key={guest.id} className="guest-item">
                <div className="guest-info">
                  <div style={{ fontWeight: '600', fontSize: '1.1rem' }}>
                    {guest.name}
                  </div>
                  <div style={{ color: '#667eea', fontWeight: '500' }}>
                    Table {guest.table}
                  </div>
                  <div style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.25rem' }}>
                    Added: {formatDate(guest.createdAt)}
                  </div>
                </div>
                <div className="guest-actions">
                  <button
                    className="btn btn-small btn-secondary"
                    onClick={() => {
                      // TODO: Implement edit functionality
                      alert('Edit functionality coming soon!');
                    }}
                    title="Edit guest"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    className="btn btn-small btn-danger"
                    onClick={() => handleDeleteGuest(guest.id)}
                    title="Delete guest"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
} 